import { DropdownOption } from "globals/store/programs.data";
import { BudgetScheduleItem, BudgetScheduleItemStatus, LineItem } from "../store/reselectors/budget.data";
import { AllSameKeyValuesEnforcer } from "./helpers";
import { tableLightColors } from "./table-helpers.data";
import { jsonCopyObject } from "globals/helpers/helpers";

export interface GridTemplateIds { gridDateSelect, gridText, gridMoney, gridStatus, gridItem, gridNumber, gridToggle, gridDropdown, gridButton }

export const gridTemplateIds: AllSameKeyValuesEnforcer<GridTemplateIds> = { 
  gridDateSelect: 'gridDateSelect', gridText: 'gridText', gridMoney: 'gridMoney',
  gridStatus: 'gridStatus', gridItem: 'gridItem', gridNumber: 'gridNumber',
  gridToggle: 'gridToggle', gridDropdown: 'gridDropdown', gridButton: 'gridButton'
}

export interface GridItem {
  text: string
  isItem: boolean
}


export type GridButtonAction = 'DELETE' | 'UPDATE'

export interface GridToggle {
  toggled: boolean
}
export interface GridText {
  text: string
  bold?: boolean
  strikethrough?: boolean
}

export interface GridDropdown {
  dropdownOption: DropdownOption
}

export interface GridMoney { // displays as 1,000.00, etc
  amount: number
  inRed?: boolean
  locked?: boolean
  showPercent?: boolean
}

export interface GridNumber { // displays as number
  amount: number
}

export interface GridDateSelect {
  date: Date
}

export type GridStatusType = BudgetScheduleItemStatus
export const gridStatusColors: { [k in GridStatusType]: string } = { Pending: tableLightColors.yellow, Paid: tableLightColors.green }

export interface GridStatus {
  status: GridStatusType
}
export interface GridTemplates {
  gridText: GridText
  gridMoney: GridMoney
  gridDateSelect: GridDateSelect
  gridStatus: GridStatus
}

export interface BaseGridData {
  id: string
  dimmed?: boolean
  seen?: 'seen' | 'unseen'
  activeDotMenuOptions?: Set<string>
}

export interface GridCol {
  id: string
  title: string
  hide?: boolean
  canAddItems?: boolean
  uneditable?: boolean
  templateId?: keyof GridTemplateIds
  placeholder?: string
  minWidth?: string
  noReadOnlyPlaceholder?: boolean
  gridTemplateColumn?: string // template column def - ie 100px / 1fr
  statusOptions?: GridStatusType[]
  defaultNumber?: number
  dropdownOptions?: DropdownOption[]
  highlighted?: boolean
}

export type GridData = BaseGridData | PaymentGridData


export interface CashFlowColumnIds { date, description, received, billed, status, balance, source }
export const cashFlowColumns: AllSameKeyValuesEnforcer<CashFlowColumnIds> = { 
  date: 'date', description: 'description', received: 'received', billed: 'billed', balance: 'balance', status: 'status',
  source: 'source'
}

export interface CashFlowGridData extends BaseGridData {
  [cashFlowColumns.date]: GridDateSelect
  [cashFlowColumns.source]: GridText
  [cashFlowColumns.description]: GridText
  [cashFlowColumns.received]: GridMoney
  [cashFlowColumns.billed]: GridMoney
  [cashFlowColumns.status]: GridStatus
  [cashFlowColumns.balance]: GridMoney
}

export const allCashFlowColumns: GridCol[] = [
  {
    title: 'Date Payment Due',
    id: cashFlowColumns.date,
    templateId: gridTemplateIds.gridDateSelect,
  }, {
    title: 'Source',
    id: cashFlowColumns.source,
    templateId: gridTemplateIds.gridText,
  }, {
    title: 'Description',
    id: cashFlowColumns.description,
    minWidth: '180px',
    templateId: gridTemplateIds.gridText,
  },  {
    title: 'Amount\nReceived',
    id: cashFlowColumns.received,
    templateId: gridTemplateIds.gridMoney,
  },  {
    title: 'Amount\nto Pay',
    id: cashFlowColumns.billed,
    templateId: gridTemplateIds.gridMoney,
  },  {
    title: 'Status',
    id: cashFlowColumns.status,
    templateId: gridTemplateIds.gridStatus,
    statusOptions: ['Pending', 'Paid']
  }, {
    title: 'Amount\nBalance',
    id: cashFlowColumns.balance,
    templateId: gridTemplateIds.gridMoney,
  }, 
]

export interface PaymentColumnIds { date, description, amount, status, percent }
export const paymentColumns: AllSameKeyValuesEnforcer<PaymentColumnIds> = { 
  date: 'date', description: 'description', amount: 'amount', percent: 'percent', status: 'status'
}
export interface PaymentGridData extends BaseGridData {
  payment: BudgetScheduleItem
  [paymentColumns.date]: GridDateSelect
  [paymentColumns.description]: GridText
  [paymentColumns.amount]: GridMoney
  [paymentColumns.percent]: GridMoney
  [paymentColumns.status]: GridStatus
}

export const allPaymentColumns: GridCol[] = [
  {
    title: 'Date Payment Due',
    id: paymentColumns.date,
    templateId: gridTemplateIds.gridDateSelect,
  }, {
    title: 'Description',
    id: paymentColumns.description,
    minWidth: '180px',
    placeholder: 'Description...',
    templateId: gridTemplateIds.gridText,
  },  {
    title: 'Amount\nReceived',
    id: paymentColumns.amount,
    placeholder: 'Amount...',
    templateId: gridTemplateIds.gridMoney,
  },  {
    title: 'Percent\nReceived',
    id: paymentColumns.percent,
    uneditable: true,
    placeholder: 'Percent...',
    templateId: gridTemplateIds.gridMoney,
  },  {
    title: 'Status',
    id: paymentColumns.status,
    templateId: gridTemplateIds.gridStatus,
    statusOptions: ['Pending', 'Paid']
  }, 
]

export interface BudgetSectionColumnIds { show, item, description, qty, qtyUpdated, rate, convertedRate, recurrence, total, status, tax, discount, fees, actionButton, linkedUnitRate, markup }
export const budgetSectionColumns: AllSameKeyValuesEnforcer<BudgetSectionColumnIds> = { 
  item: 'item', description: 'description', qty: 'qty', rate: 'rate', markup: 'markup',
  convertedRate: 'convertedRate', recurrence: 'recurrence', total: 'total',
  status: 'status', tax: 'tax', discount: 'discount', fees: 'fees', show: 'show',
  qtyUpdated: 'qtyUpdated', actionButton: 'actionButton', linkedUnitRate: 'linkedUnitRate'
}

export interface BudgetSectionGridData extends BaseGridData {
  lineItem: LineItem
  [budgetSectionColumns.item]: GridItem
  [budgetSectionColumns.description]: GridText
  [budgetSectionColumns.qty]: GridNumber
  [budgetSectionColumns.rate]: GridMoney
  [budgetSectionColumns.convertedRate]: GridMoney
  [budgetSectionColumns.linkedUnitRate]?: GridMoney
  [budgetSectionColumns.recurrence]: GridNumber
  [budgetSectionColumns.total]: GridMoney
  [budgetSectionColumns.status]: GridDropdown
  [budgetSectionColumns.tax]: GridToggle
  [budgetSectionColumns.discount]: GridToggle
  [budgetSectionColumns.fees]: GridToggle
  [budgetSectionColumns.markup]: GridToggle
  [budgetSectionColumns.show]?: GridToggle
  [budgetSectionColumns.qtyUpdated]?: GridNumber
  [budgetSectionColumns.actionButton]?: GridButtonAction
  
}

export const allRevenueColumns: GridCol[] = [
  {
    title: 'Item',
    id: budgetSectionColumns.item,
    canAddItems: true,
    placeholder: 'Item...',
    templateId: gridTemplateIds.gridItem,
  }, {
    title: 'Description',
    id: budgetSectionColumns.description,
    minWidth: '180px',
    placeholder: 'Description...',
    templateId: gridTemplateIds.gridText,
  }, {
    title: 'Qty',
    id: budgetSectionColumns.qty,
    placeholder: 'Qty...',
    templateId: gridTemplateIds.gridNumber,
    defaultNumber: 1,
  }, {
    title: 'Qty\nUpdate',
    id: budgetSectionColumns.qtyUpdated,
    placeholder: '-',
    templateId: gridTemplateIds.gridNumber,
    uneditable: true
  }, {
    title: 'Unit Rate', // this is budget currency rate in revenue and cost in costs
    id: budgetSectionColumns.convertedRate,
    placeholder: 'Rate...',
    templateId: gridTemplateIds.gridMoney
  }, {
    title: 'Unit Rate', // This is rate in revenue and cost in costs, see below for cost columns
    id: budgetSectionColumns.rate,
    placeholder: 'Rate...',
    templateId: gridTemplateIds.gridMoney
  }, {
    title: 'Unit Rate', // this is the editable rate in costs
    id: budgetSectionColumns.linkedUnitRate,
    highlighted: true,
    placeholder: 'Unit rate...',
    templateId: gridTemplateIds.gridMoney,
  }, {
    title: 'Rec',
    id: budgetSectionColumns.recurrence,
    placeholder: 'Rec...',
    templateId: gridTemplateIds.gridNumber,
    defaultNumber: null,
  }, {
    title: 'Total',
    id: budgetSectionColumns.total,
    // placeholder: 'Total...',
    templateId: gridTemplateIds.gridMoney,
    uneditable: true
  }, {
    title: 'Status',
    id: budgetSectionColumns.status,
    templateId: gridTemplateIds.gridDropdown,
    dropdownOptions: [{ id: 'Estimate', title: 'Estimate' }, { id: 'Quote', title: 'Quote' }, { id: 'Actual', title: 'Actual '}]
  }, {
    title: 'Tax',
    id: budgetSectionColumns.tax,
    templateId: gridTemplateIds.gridToggle
  }, {
    title: 'Disc',
    id: budgetSectionColumns.discount,
    templateId: gridTemplateIds.gridToggle
  }, {
    title: 'Fees',
    id: budgetSectionColumns.fees,
    templateId: gridTemplateIds.gridToggle
  }, {
    title: 'Markup',
    id: budgetSectionColumns.markup,
    templateId: gridTemplateIds.gridToggle
  }, { 
    title: '',
    id: budgetSectionColumns.actionButton,
    templateId: gridTemplateIds.gridButton,
    noReadOnlyPlaceholder: true
  }
]

export const allCostColumns = jsonCopyObject(allRevenueColumns)
allCostColumns.find(c => c.id === budgetSectionColumns.rate).title = 'Unit Cost'
allCostColumns.find(c => c.id === budgetSectionColumns.convertedRate).title = 'Unit Cost'

export const allToggleColumns = jsonCopyObject(allCostColumns)
allToggleColumns.unshift({
  title: 'Show in\nCosts',
  id: budgetSectionColumns.show,
  templateId: gridTemplateIds.gridToggle
})

export const allBillingColumns: GridCol[] = jsonCopyObject(allPaymentColumns) as GridCol[]
allBillingColumns.find(c => c.id === paymentColumns.date).title = 'Date Bill to be Paid'
allBillingColumns.find(c => c.id === paymentColumns.amount).title = 'Amount Billed'
allBillingColumns.find(c => c.id === paymentColumns.percent).title = 'Percent Billed'